import {Anchor, Group, Stack, Text, Title} from '@mantine/core';

import {useExecution} from '../../executions/hooks/useExecution.ts';
import {useOnExecutionProgress} from '../../executions/hooks/useOnExecutionProgress.ts';
import {PhaseBlock} from '../../executions/components/PhaseBlock.tsx';
import {ExecutionStatusBadge} from '../../executions/components/ExecutionStatusBadge.tsx';
import {Application, ApplicationStatus} from '../types.ts';


const SFN_REGION = 'us-west-2';

const sfnConsoleUrl = (arn: string) =>
    `https://console.aws.amazon.com/states/home?region=${SFN_REGION}#/v2/executions/details/${encodeURIComponent(arn)}`;


// FNP-79: Provisioning-progress panel for the Applications EditPage. Mounts
// only when the Application has a workflowId and is in a state where live
// progress is meaningful (CREATING is actively running; FAILED keeps the
// final phase/step rendering for diagnosis). For ACTIVE applications the
// workflow long since terminated and the progress data has rolled off the
// detail surface — that case shows a compact summary instead.
export const ApplicationProgressSection = ({application}: { application: Application }) => {

    const workflowId = application.workflowId;
    const showLiveProgress =
        !!workflowId &&
        (application.applicationStatus === ApplicationStatus.CREATING ||
            application.applicationStatus === ApplicationStatus.FAILED);

    const {record: execution} = useExecution(showLiveProgress ? workflowId : undefined);

    useOnExecutionProgress(showLiveProgress ? workflowId : undefined);

    if (!workflowId) {
        return null;
    }

    if (!showLiveProgress) {
        return (
            <Stack gap={'xs'}>
                <Title order={4}>Provisioning</Title>
                <Text size={'sm'} c={'dimmed'}>
                    This application was provisioned via workflow{' '}
                    <Text span fw={600}>{workflowId}</Text>.
                </Text>
                {application.stepFunctionsExecutionArn ? (
                    <Text size={'sm'}>
                        <Anchor
                            href={sfnConsoleUrl(application.stepFunctionsExecutionArn)}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                            size={'sm'}
                        >
                            Open execution in AWS console
                        </Anchor>
                    </Text>
                ) : null}
            </Stack>
        );
    }

    if (!execution) {
        return (
            <Stack gap={'xs'}>
                <Title order={4}>Provisioning Progress</Title>
                <Text size={'sm'} c={'dimmed'}>Loading workflow execution…</Text>
            </Stack>
        );
    }

    const phases = (execution.phases ?? []).slice().sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0),
    );

    return (
        <Stack gap={'lg'}>
            <Stack gap={'xs'}>
                <Group gap={'sm'} wrap={'wrap'}>
                    <Title order={4}>Provisioning Progress</Title>
                    <ExecutionStatusBadge status={execution.executionStatus}/>
                </Group>

                <Group gap={'lg'} wrap={'wrap'}>
                    <Text size={'sm'}>
                        <Text span fw={600}>Phases:</Text>{' '}
                        {execution.completedPhases ?? 0} / {execution.totalPhases ?? 0}
                    </Text>
                    {execution.currentPhase ? (
                        <Text size={'sm'}>
                            <Text span fw={600}>Current phase:</Text> {execution.currentPhase}
                        </Text>
                    ) : null}
                </Group>

                {application.stepFunctionsExecutionArn ? (
                    <Text size={'sm'}>
                        <Anchor
                            href={sfnConsoleUrl(application.stepFunctionsExecutionArn)}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                            size={'sm'}
                        >
                            Open execution in AWS console
                        </Anchor>
                    </Text>
                ) : null}

                {execution.failureReason || execution.failedStepName ? (
                    <Stack gap={4}>
                        {execution.failedStepName ? (
                            <Text size={'sm'} c={'red'}>
                                <Text span fw={600}>Failed step:</Text> {execution.failedStepName}
                            </Text>
                        ) : null}
                        {execution.failureReason ? (
                            <Text size={'sm'} c={'red'}>
                                <Text span fw={600}>Failure reason:</Text> {execution.failureReason}
                            </Text>
                        ) : null}
                    </Stack>
                ) : null}

                {application.statusReason ? (
                    <Text size={'sm'} c={'red'}>
                        <Text span fw={600}>Status reason:</Text> {application.statusReason}
                    </Text>
                ) : null}

                {application.applicationStatus === ApplicationStatus.FAILED && application.rollbackSummary ? (
                    <Stack gap={4}>
                        {application.rollbackSummary.rolledBackSteps.length > 0 ? (
                            <Text size={'sm'} c={'green'}>
                                <Text span fw={600}>Rolled back ({application.rollbackSummary.rolledBackSteps.length}):</Text>{' '}
                                {application.rollbackSummary.rolledBackSteps.join(', ')}
                            </Text>
                        ) : null}
                        {application.rollbackSummary.rollbackErrors.length > 0 ? (
                            <Stack gap={2}>
                                <Text size={'sm'} c={'red'} fw={600}>
                                    Rollback errors ({application.rollbackSummary.rollbackErrors.length}) — these resources may be orphaned:
                                </Text>
                                {application.rollbackSummary.rollbackErrors.map((re) => (
                                    <Text key={re.step} size={'sm'} c={'red'}>
                                        • {re.step}: {re.error}
                                    </Text>
                                ))}
                            </Stack>
                        ) : null}
                    </Stack>
                ) : null}
            </Stack>

            <Stack gap={'lg'}>
                {phases.length === 0 ? (
                    <Text size={'sm'} c={'dimmed'}>No phases recorded yet.</Text>
                ) : (
                    phases.map((phase) => <PhaseBlock key={phase.id} phase={phase}/>)
                )}
            </Stack>
        </Stack>
    );
};
